/**
 * English locale. Single source of truth for every user-facing
 * string. Add new keys here as the UI grows.
 *
 * Convention: keys are dot-notation in calls (`t('auth.login.submit')`)
 * but objects in this file (`auth.login.submit`). Keep nesting
 * shallow (3 levels max) — flatter trees are easier to grep + refactor.
 *
 * `as const` is intentional — narrows the inferred type so future
 * locales can be type-checked against this shape (use `typeof` of
 * this default as the structural contract).
 */
export default {
  app: {
    name: 'HonestWaves Console',
    error: {
      generic: 'Something went wrong',
      unexpected: 'Unexpected error',
      requestFailed: 'Request failed',
      sessionEnded: 'Your session has ended. Please sign in again.',
      session_expired:
        'Your browser session is out of sync with the server. Please reload the page.',
      networkOffline: 'Network unreachable',
    },
    loading: 'Loading…',
  },
  auth: {
    login: {
      title: 'Sign in',
      subtitle: 'Welcome back. Use your work email to continue.',
      email: 'Work email',
      password: 'Password',
      submit: 'Sign in',
      failure: 'Login failed',
      forgotPassword: 'Forgot your password?',
      or: 'or',
      google: 'Sign in with Google',
      ssoError: {
        no_account: 'No account is registered for that Google address.',
        not_authorized: 'That account is not permitted to sign in here.',
        invalid_state: 'Your sign-in session expired — please try again.',
        denied: 'Google sign-in was cancelled.',
        no_email: 'Google did not return an email address.',
        exchange_failed: 'Could not complete Google sign-in — please try again.',
        generic: 'Single sign-on failed — please try again.',
      },
    },
    forgotPassword: {
      title: 'Forgot password',
      intro: "Enter your email; we'll send a reset link.",
      emailPlaceholder: 'Email',
      submit: 'Send reset link',
      success: 'If the email is registered, a reset link has been sent.',
      checkInbox: 'Check your inbox for instructions.',
      failure: 'Unable to send reset link',
      backToLogin: 'Back to sign in',
    },
    resetPassword: {
      title: 'Reset password',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      submit: 'Reset password',
      success: 'Password reset. Sign in with your new password.',
      failure: 'Reset failed',
      mismatch: 'Passwords do not match',
    },
  },
  nav: {
    dashboard: 'Dashboard',
    kiosks: 'Lockers',
    missionControl: 'Mission Control',
    otaUpdates: 'Software updates',
    devices: 'Devices',
    deviceModels: 'Device models',
    users: 'Users',
    groups: 'Departments',
    bayAccess: 'Access matrix',
    orders: 'Orders',
    activity: 'Activity',
    notifications: 'Notifications',
    tenants: 'Tenants',
    onPremClients: 'On-Prem Clients',
    mdmProfiles: 'MDM Profiles',
    systemLogs: 'System Logs',
    tracker: 'Tracker',
    reports: 'Reports',
    activityLog: 'Activity log',
    syncLog: 'Sync log',
    deviceStatus: 'Device status',
    assetAccounting: 'Asset Accounting',
    configs: 'Settings',
    profile: 'Profile',
    logout: 'Sign out',
  },
  missionControl: {
    heading: 'Mission Control',
    description:
      'Live bay state and per-bay control for a selected locker. Refreshes every 30 seconds while a locker is selected.',
    selectPlaceholder: 'Select a locker…',
    emptyState: 'Pick a locker on the left to see live bay state.',
    editingPausedPoll: 'Live refresh paused while you have unsaved bay changes.',
    refreshing: 'Refreshing…',
    saving: 'Saving…',
    usage:
      'Pick a locker to watch its bays live. The view auto-refreshes every 30 seconds. Tap a bay to open its detail.',
    applyStates: 'Update',
    pendingStates: '{count} pending',
    lockerConfig: 'Locker settings',
    offlineBanner:
      "This locker is offline — it hasn't checked in recently. Bay controls are disabled until it reconnects.",
    avgVoltage: 'Avg voltage',
    avgCurrent: 'Avg current',
    avgPower: 'Avg power',
    serialPrefix: 'Serial',
  },
  ota: {
    title: 'Software updates',
    subtitle: 'Push an application update to selected lockers or entire locations.',
    selectKiosks: 'Pick locations or individual lockers',
    selectAll: 'Select all',
    clearAll: 'Clear selection',
    unassigned: 'No location',
    unknownVersion: 'unknown',
    refresh: 'Update version list',
    refreshed: 'Version list updated.',
    downloadApk: 'Download APK',
    downloadFailed: 'APK download failed.',
    release: {
      placeholder: 'Select version',
      empty: 'No releases available yet',
    },
    notes: {
      title: 'Details',
      empty: 'No release notes for this version.',
    },
    dispatch: {
      button: 'Update',
      confirmHeader: 'Send software update?',
      confirm:
        'Push {version} to {count} lockers? Each downloads it and installs it once no one is using the locker.',
      queued: 'Update sent to {count} lockers.',
      noTargets: 'Select at least one locker.',
    },
    cancel: {
      button: 'Cancel',
      confirmHeader: 'Cancel this update?',
      confirm: 'Stop the update to {version} on {locker}? The locker keeps the version it has now.',
      sent: 'Cancel sent to {locker}.',
    },
    progress: {
      title: 'Update progress',
      empty: 'No OTA dispatches in the last 7 days.',
      kiosk: 'Locker',
      original: 'Original',
      target: 'Target',
      status: 'Status',
    },
  },
  syncActivity: {
    title: 'Locker sync',
    history: 'Recent syncs',
    viewAll: 'View all',
    moreActive: '+{count} more',
    lockersProgress: '{acked}/{total} locker | {acked}/{total} lockers',
    offline: '{count} offline',
    failedCount: '{count} failed',
    status: {
      in_progress: 'Syncing',
      done: 'Synced',
      partial: 'Partly synced (some lockers offline)',
      failed: 'Failed on some lockers',
    },
    kind: {
      data: 'Data change',
      config: 'Locker config',
      branding: 'Branding',
      ota: 'App update',
    },
  },
  syncLog: {
    title: 'Sync log',
    subtitle: 'Every sync to your lockers and whether each locker received it.',
    search: 'Search syncs or who made them',
    filters: {
      allKinds: 'All kinds',
      allStatuses: 'All statuses',
    },
    range: {
      day: 'Last 24 hours',
      week: 'Last 7 days',
      all: 'All',
    },
    columns: {
      sync: 'Sync',
      actor: 'By',
      lockers: 'Lockers',
      started: 'Started',
    },
    empty: 'No syncs in this range',
    detail: {
      started: 'Started',
      finished: 'Finished',
      lockers: 'Lockers',
      received: 'Received {ago}',
      failed: 'Failed',
      pending: 'Pending',
      notReceived: 'Not received',
      noLockers: 'No lockers recorded for this sync.',
    },
  },
  notifications: {
    title: 'Notifications',
    placeholder: 'Notification settings — coming soon.',
    list: {
      label: 'Notifications list',
    },
    search: {
      placeholder: 'Search notifications…',
    },
    empty: {
      title: 'No notifications',
      body: 'When something happens, it shows up here.',
    },
    markAllRead: 'Mark all as read',
    sort: {
      newest: 'Newest first',
      oldest: 'Oldest first',
      severityHigh: 'Severity — high to low',
      severityLow: 'Severity — low to high',
    },
    filters: {
      trigger: 'Filters',
      triggerWithCount: 'Filters ({count})',
      popoverLabel: 'Notification filters',
      // Lead-in label for the active-filter chips strip below the toolbar.
      activeLabel: 'Filters:',
      // Section labels (titles inside the popover).
      sections: {
        dateRange: 'Date range',
        severity: 'Severity',
        quickGroups: 'Quick groups',
        kind: 'Notification kind',
        readState: 'Read state',
      },
      // One-click log-type group shortcuts.
      groups: {
        deviceMoves: 'Device moves',
      },
      kindPlaceholder: 'Any kind',
      // Date-range shortcut option labels.
      range: {
        today: 'Today',
        '7d': 'Last 7 days',
        '30d': 'Last 30 days',
        custom: 'Custom',
      },
      // Read-state option labels.
      readState: {
        all: 'All',
        unread: 'Unread only',
        read: 'Read only',
      },
    },
    bulk: {
      selected: '{count} selected',
      markRead: 'Mark read',
      markUnread: 'Mark unread',
      clear: 'Clear',
      selectRow: 'Select notification',
    },
    columns: {
      severity: 'Severity',
      title: 'Notification',
      kind: 'Kind',
      time: 'When',
      unread: 'Unread',
      read: 'Read',
    },
    detail: {
      markRead: 'Mark as read',
      markUnread: 'Mark as unread',
      noSelection: {
        title: 'Pick a notification',
        body: 'Select a notification on the left to see its details here.',
      },
    },
    body: {
      empty: 'No additional details.',
      noBespokeRenderer: 'No bespoke renderer for "{kind}" — showing raw payload.',
    },
    guestBody: {
      status: {
        approved: 'Approved',
        denied: 'Denied',
      },
      targetNotified: 'User notified via web inbox',
      fields: {
        target: 'Guest',
        employeeId: 'Employee ID',
        decidedBy: 'Decided by',
        tenant: 'Tenant',
        decidedAt: 'When',
      },
    },
  },
  systemLogs: {
    title: 'System Logs',
    placeholder: 'System logs — coming soon.',
  },
  tracker: {
    title: 'Tracker',
    loading: 'Loading trace…',
    loadMore: 'Load more',
    clearAnchor: 'Clear anchor',
    markerHint: 'Click to anchor on this entity',
    noResults: 'No nodes in this trace window.',
    searchHeading: 'Pick what to trace',
    banner: {
      tag: 'Tracker',
      lead: 'Unified trace.',
      body: 'Every order, bay state, device move, and activity-log event for the anchored entity — merged into one timeline.',
      hint: 'Click any timeline item for detail.',
    },
    timeline: {
      today: 'Today',
      yesterday: 'Yesterday',
      eventCount: '{count} event | {count} events',
    },
    emptyResult:
      'No matching events for this anchor in the current filter and time range. Adjust filters or extend the date range to see more.',
    filters: {
      entity: 'Entities',
      events: 'Events',
      viewOnly: 'Show only',
      from: 'From',
      to: 'To',
      fromPlaceholder: 'Start',
      toPlaceholder: 'End',
      families: {
        order: 'Order events',
        bay: 'Bay states',
        device: 'Device moves',
        other: 'Other',
      },
    },
    detail: {
      title: 'Trace node',
      entityBadge: 'Entity',
      jumpTo: 'Re-anchor on',
      relatedSection: 'Related entities',
      entitySections: {
        customer: 'Customer',
        operator: 'Operator',
        device: 'Device',
      },
      deviceFields: {
        model: 'Model',
        state: 'State',
        nfc: 'NFC tag',
        rfid: 'RFID tag',
        condition: 'Condition',
        serial: 'Serial number',
      },
      userFields: {
        employeeId: 'Employee ID',
        email: 'Email',
      },
      orderFields: {
        opened: 'Opened',
        closed: 'Closed',
        items: 'Devices',
      },
    },
    anchor: {
      badge: 'Tracing',
      events: 'event | events',
      days: 'day | days',
      devices: 'device | devices',
      clear: 'Clear trace',
      kinds: {
        user: 'User',
        device: 'Device',
        bay: 'Bay',
        kiosk: 'Locker',
        order: 'Order',
      },
    },
    empty: {
      heading: 'Pick an entity to trace',
      body: 'View the complete activity history for a user, device, bay, locker, or order. Select a record to begin.',
    },
    picker: {
      anchorKind: 'Entity type',
      confirm: 'Lookup',
      kinds: {
        user: 'User',
        device: 'Device',
        bay: 'Bay',
        kiosk: 'Locker',
      },
      labels: {
        type: 'Type',
        kiosk: 'Locker',
        user: 'Search user',
        device: 'Search device',
        bay: 'Bay',
        bayNumber: 'Bay number',
        kioskSearch: 'Search locker',
        from: 'From',
        to: 'To',
      },
      userHelp: 'Search by email, employee ID, person ID, or name',
      userPlaceholder: 'Start typing email or ID…',
      deviceHelp: 'Search by serial number, NFC tag, or RFID tag',
      devicePlaceholder: 'Start typing serial / NFC / RFID…',
      bayHelp: 'Pick the locker first, then the bay number',
      bayKioskPlaceholder: 'Search by locker serial or name…',
      bayNumberPlaceholder: 'Pick a bay…',
      bayPickKioskFirst: 'Pick a locker first',
      kioskHelp: 'Search by locker serial or name',
      kioskPlaceholder: 'Start typing serial or name…',
    },
    body: {
      orderTx: {
        kind: {
          checkout: 'Checkout',
          checkin: 'Checkin',
          cancel: 'Cancelled',
          admin_override: 'Admin override',
          alarm: 'Alarm',
          error: 'Error',
          open: 'Opened',
          reset: 'Reset',
          lock: 'Locked',
          system: 'System event',
        },
        fields: {
          orderId: 'Order',
          kioskId: 'Locker',
          passcodeHint: 'Passcode',
          notes: 'Notes',
        },
      },
      bayState: {
        fields: {
          kioskId: 'Locker',
        },
      },
      deviceMove: {
        assigned: 'Assigned',
        removed: 'Removed',
        fields: {
          deviceId: 'Device',
          bayId: 'Bay',
          assignedBy: 'Placed by',
          removedBy: 'Removed by',
        },
      },
      activity: {
        via: 'via',
        fields: {
          relatedTo: 'Related to',
        },
        ooiFields: {
          id: 'ID',
          cause: 'Cause',
          by: 'By',
        },
      },
      orderEntity: {
        fields: {
          user: 'Customer',
          openedAt: 'Opened',
          closedAt: 'Closed',
        },
      },
    },
  },
  reports: {
    title: 'Activity log',
    placeholder: 'Reports — coming soon.',
    subtitle: 'Every event from your fleet, in plain English.',
    // KPI strip (5 tiles) — real-data 7-day aggregation.
    kpis: {
      events7d: 'Events · 7 days',
      eventsVsPrior: 'vs {n} prior 7d',
      actions: 'Actions',
      routines: 'Routines',
      failures: 'Failures & faults',
      commands: 'Commands',
    },
    // 24h "by Kind" chart.
    chart: {
      title: 'Events over the last 24 hours',
      byKind: 'by Kind',
      ago24h: '24h ago',
      h18: '18h',
      h12: '12h',
      h6: '6h',
      now: 'now',
      empty: 'No events in the last 24 hours.',
    },
    filters: {
      searchPlaceholder: 'Search by event title, user, device serial, or order #…',
      userLabel: 'User',
      userPlaceholder: 'Search user…',
      deviceLabel: 'Device',
      devicePlaceholder: 'Search device…',
      kioskLabel: 'Locker',
      kioskPlaceholder: 'Search locker…',
      bayLabel: 'Bay',
      bayPlaceholder: 'Pick a bay',
      bayDisabledPlaceholder: 'Select a locker first',
      locationLabel: 'Location',
      departmentLabel: 'Department',
      timeFromLabel: 'From',
      timeToLabel: 'To',
      timeFromPlaceholder: 'Earliest',
      timeToPlaceholder: 'Latest',
      anchorHint: 'Picking a user, device, or locker scopes the trace to that entity.',
      showRecordChange: 'Show record change',
      showRecordChangeHint:
        'Include record-change audit entries (create / update / delete of users, devices, lockers, and other records). Hidden by default.',
      reset: 'Reset',
      apply: 'Apply',
    },
    columns: {
      time: 'Time',
      kind: 'Kind',
      category: 'Type',
      event: 'Event',
      actor: 'Actor',
      user: 'User',
      lockerBay: 'Locker · Bay',
      device: 'Device',
      relations: 'Relations',
      summary: 'Summary',
    },
    category: {
      action: 'Action',
      event: 'Event',
      state_change: 'State change',
      routine: 'Routine',
      command: 'Command',
    },
    // Per-event detail drawer (info-only).
    drawer: {
      whatHappened: 'What happened',
      firstSeen: 'First seen',
      affectedFor: 'Affected for',
      recurrence: 'Recurrence',
      recurrenceFirst: 'First today',
      affected: 'Affected',
      relatedTimeline: 'Related events',
      relatedTimelineScoped: 'Related events on {target}',
      thisEvent: 'this event',
      sourceMeta: 'Source & metadata',
      logType: 'Catalog type',
      eventId: 'Event ID',
      source: 'Source',
      severity: 'Severity',
      empty: 'No additional detail.',
    },
    none: '—',
    system: '— system —',
    multiple: 'multiple',
    // Human-readable names for wire-token entity kinds. Used in
    // chips (Anchors column) and Actor cells. Tokens not in this
    // map fall back to title-cased token.
    kindLabel: {
      user: 'User',
      device: 'Device',
      bay: 'Bay',
      kiosk: 'Locker',
      order: 'Order',
      system: 'System',
    },
    // Relative-time vocabulary for the Time column.
    time: {
      justNow: 'just now',
      secondsAgo: '{n}s ago',
      minutesAgo: '{n}m ago',
      hoursAgo: '{n}h ago',
      yesterday: 'yesterday',
      daysAgo: '{n}d ago',
    },
    pagination: {
      prev: 'Previous',
      next: 'Next',
      page: 'Page {n}',
      sizeLabel: 'Rows per page',
    },
    chipHintReanchor: 'Click to filter on this entity',
    loading: 'Loading…',
    empty: 'No results match the current filters. Clear the anchor or widen the date range.',
    export: 'Export to Excel',
    exportTooltip:
      'Streamed XLSX download. Capped at 50,000 rows — narrow the filter if the export truncates.',
    exportStarting: 'Preparing export…',
  },
  deviceStatusReport: {
    title: 'Device Status Report',
    subtitle:
      'Devices that haven’t been returned, are damaged, or were returned but not plugged in — by location and department.',
    filters: {
      searchPlaceholder: 'Search device or user…',
      allLocations: 'All locations',
      allDepartments: 'All departments',
      allStatuses: 'All statuses',
      overdueOnly: 'Overdue only',
    },
    columns: {
      location: 'Location',
      department: 'Department',
      device: 'Device',
      status: 'Status',
      user: 'User',
      locker: 'Locker',
      bay: 'Bay',
      overdueDuration: 'Overdue for',
      lastUpdate: 'Last update',
    },
    statuses: {
      overdue: 'Overdue',
      checked_out: 'Checked out',
      damaged: 'Damaged',
      not_charging: 'Not charging',
      lost: 'Lost',
      retired: 'Retired',
      available: 'Available',
    },
    charts: {
      breakdown: 'Status breakdown',
      byLocation: 'By location',
      byDepartment: 'By department',
    },
  },
  configs: {
    title: 'Settings',
    placeholder: 'Configuration — coming soon.',
    navAriaLabel: 'Settings sections',
    sections: {
      profile: {
        label: 'Profile',
        description: 'Your account identity and sign-in.',
      },
      general: {
        label: 'General',
        description: 'Tenant-wide settings — time zone.',
      },
      branding: {
        label: 'Branding',
        description: 'Tenant logo and brand assets shown to operators and customers.',
        placeholder: 'Branding controls — coming soon.',
      },
      deletionReasons: {
        label: 'Device deletion reasons',
        description: 'Reasons support staff pick from when deleting a phone.',
      },
      notifications: {
        label: 'Notifications',
        description: 'Late-device return reminders sent to users.',
      },
      scheduledReports: {
        label: 'Scheduled reports',
        description: 'Recurring device summaries emailed to supervisors.',
      },
      integrations: {
        label: 'Integrations',
        description: 'Connect third-party directories and services.',
      },
    },
    profile: {
      // ── Identity card ────────────────────────────────────────
      identityHeading: 'Identity',
      identityDescription:
        'Your name and email — how the system addresses you and how teammates find you.',
      firstNameLabel: 'First name',
      lastNameLabel: 'Last name',
      emailLabel: 'Email',
      emailHelp: 'Used to sign in and to receive notifications.',
      identitySaved: 'Profile updated.',
      identitySave: 'Save changes',
      // ── Read-only fields shown alongside the form ────────────
      personIdLabel: 'Personal ID',
      personIdHelp: 'Assigned by your organization — contact an admin to change.',
      rolesLabel: 'Roles',
      // ── Password card ────────────────────────────────────────
      passwordHeading: 'Password',
      passwordDescription: "Change your password. You'll stay signed in.",
      newPasswordLabel: 'New password',
      newPasswordConfirmLabel: 'Confirm new password',
      passwordSaved: 'Password updated.',
      passwordSave: 'Update password',
      passwordsMismatch: 'New password and confirmation must match.',
      // ── Email notifications card ─────────────────────────────
      notificationsHeading: 'Email notifications',
      notificationsDescription:
        'Choose which event emails you receive. Turning one off only affects your own inbox.',
      notificationsSaved: 'Notification preferences updated.',
    },
    general: {
      timezone: 'Time zone',
      timezoneHint:
        'Used for times in emails, the scheduled report send time and exported spreadsheets. Times shown on screen follow your browser.',
      saved: 'General settings saved.',
    },
    deletionReasons: {
      description:
        'Customize the reasons support staff choose from when deleting a device. Disabled reasons stay on past deletions but are hidden from new ones.',
      labelPlaceholder: 'e.g. Damaged screen',
      enabled: 'Enabled',
      add: 'Add reason',
      empty: 'No reasons yet. Add one below.',
      moveUp: 'Move up',
      moveDown: 'Move down',
      saved: 'Deletion reasons saved.',
    },
    notifications: {
      description:
        'Email the user who checked out a device when it becomes overdue, and customize that message. Reminders repeat about once a day until the device is returned. Off by default — no one is emailed until you enable it.',
      enable: 'Send late-device reminders',
      overdueAfterMinutes: 'Notify after',
      minutes: 'minutes overdue',
      subject: 'Email subject',
      body: 'Email message',
      insertHint: 'Insert a placeholder (replaced with the real value when the email is sent):',
      saved: 'Notification settings saved.',
    },
    scheduledReports: {
      description:
        'Email a device-checkout summary (as a spreadsheet) to your supervisors on a schedule. Off by default — nothing is sent until you enable it.',
      enable: 'Send scheduled reports',
      frequency: 'Frequency',
      daily: 'Daily',
      weekly: 'Weekly',
      dayOfWeek: 'Day',
      sendAt: 'Send at',
      overdueOnly: 'Overdue devices only',
      overdueOnlyHint:
        'When on, the report lists only overdue checkouts — keeping it short and reducing noise.',
      recipientsNote: 'Reports go to all managers and admins who have an email on file.',
      saved: 'Scheduled report settings saved.',
      days: {
        mon: 'Monday',
        tue: 'Tuesday',
        wed: 'Wednesday',
        thu: 'Thursday',
        fri: 'Friday',
        sat: 'Saturday',
        sun: 'Sunday',
      },
    },
    branding: {
      companyNameLabel: 'Company name',
      companyNameDescription:
        'Displayed in the operator UI header, customer-facing surfaces, and outbound notifications. Lockers show it at the top of the screen when no Site logo is set.',
      companyNamePlaceholder: 'e.g. Acme Lockers',
      replace: 'Replace',
      replaceAriaLabel: 'Replace {name}',
      defaultBadge: 'Default',
      resetToDefault: 'Revert to default',
      assetUploaded: 'Asset updated.',
      assetReset: 'Reverted to default.',
      companyNameSaved: 'Company name saved.',
      removeBeforeUpload: 'Remove the current image before uploading a new one.',
      upload: {
        acceptHint: 'Allowed: {types}. Max {max} KB.',
      },
      // Sub-section grouping — Shared / Portal / Locker. Each
      // bucket renders as its own card; allocation is driven by
      // `BrandingAsset::scope()` on the BE.
      scopes: {
        shared: {
          label: 'Organization brand',
        },
        portal: {
          label: 'Management portal',
          description: 'Assets shown only inside the operator-facing admin portal.',
          emptyState: 'No portal-specific assets yet — additions will appear here.',
        },
        locker: {
          label: 'Locker app',
        },
      },
      assets: {
        logo: {
          label: 'Site logo',
          description:
            'Header chrome on the operator UI and customer surfaces, and centred at the top of every locker screen.',
        },
        app_icon: {
          label: 'App icon',
          description: 'Browser favicon and PWA / installed-app icon.',
        },
        splash: {
          label: 'Splash screen',
          description: 'Welcome image shown when the locker wakes.',
        },
        screensaver: {
          label: 'Screensaver',
          description: 'Idle image shown when the locker is unattended.',
        },
        ac_checkin: {
          label: 'Deposit button (Access-code)',
          description: 'Image / video on the AC-mode "Deposit" action button.',
        },
        ac_checkout: {
          label: 'Pickup button (Access-code)',
          description: 'Image / video on the AC-mode "Pickup" action button.',
        },
        dc_checkin: {
          label: 'Check-in button (Device-scan)',
          description: 'Image / video on the DC-mode "Check in" action button.',
        },
        dc_checkout: {
          label: 'Check-out button (Device-scan)',
          description: 'Image / video on the DC-mode "Check out" action button.',
        },
        hybrid_public: {
          label: 'Mode selector — Anonymous',
          description: 'Image / video on the Hybrid-home "Public" selector button.',
        },
        hybrid_assigned: {
          label: 'Mode selector — Direct assignment',
          description: 'Image / video on the Hybrid-home "Assigned" selector button.',
        },
        device_sample: {
          label: 'Device sample',
          description: 'Reference photo of a sample device shown in help / onboarding.',
        },
        scan_sample: {
          label: 'Scan sample',
          description: 'Reference photo demonstrating the correct scan gesture.',
        },
      },
    },
  },
  integrations: {
    // Group-scoped sync (Entra): sync only selected directory groups.
    scope: {
      impact:
        '{inScope} member(s) in the selected group(s). {departing} currently-synced member(s) fall outside this scope and will lose locker access on the next sync (their accounts are kept, and widening the scope restores them).',
    },
    attributeMap: {
      usingDefault: 'Using the default column',
      defaultHint: 'default: {column}',
      noneDetected:
        'No columns detected yet. Save your credentials and test the connection, then reopen this panel to load the columns from your directory.',
    },
    sectionDescription: 'Connect third-party directories and services to sync users and groups.',
    addButton: 'Add integration',
    emptyState: 'No integrations configured yet. Add one to get started.',
    enabledLabel: 'Enabled',
    advancedLabel: 'Advanced',
    setupTitle: 'Connection setup',
    writeThroughWarning:
      'When on, members and groups created, changed, or removed in the locker are pushed to Entra — including creating and permanently deleting the matching Entra account or group. The directory stays the source of truth (Entra → locker is always authoritative). Leave off for one-way sync only.',
    saved: 'Integration saved.',
    removed: '{name} removed.',
    removeButton: 'Remove integration',
    syncQueued: 'Sync queued.',
    testButton: 'Test connection',
    syncButton: 'Sync now',
    lastSynced: 'Last synced',
    lastResult: {
      label: 'Last sync',
      created: '{n} created',
      updated: '{n} updated',
      departed: '{n} departed',
    },
    status: {
      connected: 'Connected',
      disconnected: 'Disconnected',
      error: 'Error',
    },
    test: {
      successTitle: 'Connection OK',
      success: 'Connection successful — {count} records found.',
      failTitle: 'Connection failed',
    },
    removeConfirm: {
      header: 'Remove integration?',
      message: 'Remove {name}? The connection and its configuration will be deleted.',
      accept: 'Remove',
    },
    capabilities: {},
  },
  views: {
    dashboard: {
      welcome: 'Welcome, {name}',
      body: 'HonestWaves admin dashboard. Choose a resource from the sidebar to manage it.',
    },
    error: {
      goHome: 'Back to dashboard',
    },
  },
  header: {
    toggleSidebar: 'Toggle sidebar',
    switchToDark: 'Switch to dark mode',
    switchToLight: 'Switch to light mode',
    accessLookup: 'Access lookup',
    notifications: 'Notifications ({count} unread)',
  },
  table: {
    loadFailed: 'Failed to load',
    columnId: 'ID',
    columnRow: 'Row',
    columnActions: 'Actions',
    columnStatus: 'Status',
    columnCreatedAt: 'Created',
    columnUpdatedAt: 'Updated',
    columnNotes: 'Notes',
  },
  resources: {
    kiosks: 'Lockers',
    devices: 'Devices',
    deviceModels: 'Device models',
    locations: 'Locations',
    kioskTemplates: 'Locker templates',
    users: 'Users',
    groups: 'Departments',
    bayAccess: 'Access matrix',
    orders: 'Orders',
    activityLog: 'Activity log',
    comingSoon: 'Coming soon',
  },
  notification: {
    dismiss: 'Dismiss',
    titles: {
      error: 'Something went wrong',
      success: 'Success',
    },
    messages: {
      created: '{name} created.',
      updated: '{name} updated.',
      deleted: '{name} deleted.',
      commandIssued: 'Command published to locker.',
      roleAssigned: 'Role assigned.',
      roleRevoked: 'Role revoked.',
      masterKeyRevoked: 'Master key revoked.',
      passcodeCopied: 'Passcode copied to clipboard.',
    },
  },
  // USER impersonation - a platform super_admin viewing the app as another
  // user (NOT the operating-tenant picker, which keeps your own identity).
  impersonation: {
    banner: {
      viewingAs: 'Viewing as {name}',
      asOperator: '(impersonated by {operator})',
      revert: 'Return to your account',
    },
    action: {
      label: 'Impersonate',
      tooltip: 'View the app as this user',
      confirmHeader: 'Impersonate {name}?',
      confirmBody:
        'You will act as this user with their full permissions, and every action will be recorded against your account. Return any time from the banner.',
      confirmAccept: 'Impersonate',
      started: 'You are now viewing as {name}',
      stopped: 'Returned to your account',
    },
  },

  common: {
    created: 'Created',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    inUseByOrder: 'Cannot delete — in use by an in-flight order',
    editLockedNote:
      'Locked — an in-flight order depends on this. Close or complete the order to edit.',
    editLockedUserNote:
      'Locked by an in-flight order — only email, PIN, and multi-checkout can be changed.',
    edit: 'Edit',
    view: 'View',
    create: 'Create',
    add: 'Add',
    remove: 'Remove',
    apply: 'Apply',
    clear: 'Clear',
    noResults: 'No matches',
    createWithName: 'Add {name}',
    editWithName: 'Edit {name}',
    search: 'Search',
    loading: 'Loading',
    empty: 'No data',
    sortControl: 'Sort',
    sortPlaceholder: 'Sort by…',
    sortAsc: 'Ascending',
    sortDesc: 'Descending',
    sortDirection: 'Sort direction',
    yes: 'Yes',
    no: 'No',
    none: '—',
    optional: 'optional',
    back: 'Back',
    close: 'Close',
    open: 'Open',
    actions: 'Actions',
    confirmDelete: {
      title: 'Delete this {name}?',
      message: 'This action is permanent and cannot be undone.',
      accept: 'Delete',
      reject: 'Cancel',
    },
    fields: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      status: 'Status',
      notes: 'Notes',
      createdAt: 'Created at',
      updatedAt: 'Updated at',
    },
  },
  // ── Resource: User ─────────────────────────────────────────────
  user: {
    title: 'User',
    new: 'Add user',
    searchPlaceholder: 'Search name, email, Badge ID…',
    webAccess: 'Web access',
    webAccessHelp:
      'Allow this user to log into the admin portal (subject to role grants). Locker-only users have this off.',
    passwordKeepCurrent: 'Leave blank to keep the current password.',
    lockerCountTip: 'Lockers this user has access to (direct + via departments).',
    groupCountTip: 'Departments this user belongs to.',
    lostDevicesTip: "Devices marked lost in this user's closed orders (count and total value).",
    userIdentity: 'User identity',
    tenantIdentity: 'Tenant identity',
    detailsHeading: 'User details',
    editorNav: 'User editor sections',
    access: {
      heading: 'Access config',
      lockerAccessControl: 'Locker Access Control',
      pickRoleFirst: 'Pick a role to configure access.',
      adminImplicit:
        'Tenant admins have implicit access to every locker and bay — no per-bay grants needed.',
      guestRestricted:
        'Guests are locker-only and don’t carry operator-managed bay or department grants.',
      bayAssignments: 'Bay assignments',
      groupMemberships: 'Grant access using Departments',
      groupSubtitle: 'Bays inherited from the user’s departments.',
      groupBanner:
        'Add this user to one or more departments to grant access in bulk — they inherit every bay each department is granted, across all lockers. Manage which bays a department covers on the department itself.',
      groupInheritToggle: 'Inherit bays from {name}',
      groupInheritOn: 'Inherits bays',
      groupInheritOff: 'Member only',
      locationAssignments: 'Location assignments',
      kioskAssignments: 'Locker assignments',
      pickGroups: 'Pick departments…',
      pickLocations: 'Pick locations…',
      pickKiosks: 'Pick lockers…',
      bayN: 'Bay {n}',
      // BayGrantPanel — accordion-per-locker pattern.
      noLockersYet: 'No bay grants yet. Add a locker below to start.',
      addLocker: 'Add locker',
      addLockerPlaceholder: 'Pick a locker to add…',
      removeLocker: 'Remove this locker',
      lockerHasNoBays: 'This locker has no bays configured.',
      bayCountSummary: '{granted} of {total} bays',
      bayCountPartial: '{granted} bays granted',
      pickBaysPlaceholder: 'Pick bays…',
      baySelectedCount: '{n} selected',
      // Per-locker mode toggle in BayGrantPanel.
      bayModeSpecific: 'Specific bays',
      bayModeAll: 'All bays',
      bayAllSummary: 'All bays',
      bayAllHelp: 'User has access to every bay of this locker.',
      // LocationGrantPicker.
      locationGrantHelp:
        '{n} locations granted. Access resolves through location → lockers → bays.',
      // KioskAssignmentPicker.
      kioskAssignmentHelp:
        '{n} lockers assigned. The supervisor administers these lockers and holds every bay on them.',
      // Access type SelectButton (user role).
      typeHeading: 'Grant Explicit Access',
      typeSubtitle: 'Full access to every bay, or only selected bays.',
      typeLabel: 'How does this user’s access primarily resolve?',
      typeNone: 'None',
      typeAllAccess: 'Full access',
      typeSelectedBays: 'Selected bays',
      typeAllAccessBanner:
        'Full access lets this user use every bay on every locker in the tenant, with no per-bay setup. Use it for trusted staff who need unrestricted access.',
      typeSelectedBaysBanner:
        'Pick exactly which bays this user can use, per locker, below. They can use only the bays you grant here.',
      typeGroups: 'Departments',
    },
    multiCheckoutHelp: 'Allow holding more than one locker at the same time.',
    selectableBaysHelp: 'Let the user choose which of their bays to open, instead of the kiosk picking one. Required to open Inventory bays.',
    accessPinKeepCurrent: 'Leave blank to keep the current PIN. 4–12 digits to replace it.',
    lockerAdminPinKeepCurrent:
      'Opens admin mode on the locker at this member’s role level. Leave blank to keep the current PIN. Exactly 8 digits to replace it.',
    activationCodeHelp:
      'While set, the member activates at a locker: they enter this code, then scan their badge. 4–32 letters and digits.',
    accessPinBlankPlaceholder: '••••••',
    showCurrentPin: 'Show current PIN',
    hideCurrentPin: 'Hide current PIN',
    currentPin: 'Current PIN',
    tabs: {
      users: 'Users',
      groups: 'Departments',
      guests: 'Guests',
    },
    status: {
      active: 'Active',
      disabled: 'Disabled',
      deleted: 'Deleted',
    },
    filter: {
      allStatuses: 'All statuses',
      allAccess: 'All access',
      allRoles: 'All roles',
      allTenants: 'All tenants',
      webOnly: 'Web access',
      lockerOnly: 'Locker-only',
    },
    fields: {
      name: 'Name',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      personId: 'Personal ID',
      directoryObjectId: 'Directory object ID',
      password: 'Password',
      status: 'Status',
      role: 'Role',
      lastLogin: 'Last login',
      roles: 'Roles',
      employeeId: 'Employee ID',
      activationCode: 'Activation code',
      nfcTag: 'NFC tag',
      rfidTag: 'RFID tag',
      accessPin: 'Access PIN',
      lockerAdminPin: 'Locker admin PIN',
      multiCheckout: 'Multi-checkout',
      selectableBays: 'Selectable bays',
      unreturned: 'In-flight',
      user: 'User',
      access: 'Access',
      department: 'Department',
      ids: 'IDs',
      lastSignIn: 'Last sign-in',
      neverSignedIn: 'Never',
    },
    actions: {
      manageRoles: 'Manage roles',
    },
    counts: {
      total: 'Total users',
      ofUsers: 'of users',
      pendingGuests: 'Pending guests',
      pendingSub: 'awaiting approval',
      lockedDisabled: 'Locked / disabled',
      lockedDisabledSub: '{locked} locked · {disabled} disabled',
      unreturnedDevices: 'Unreturned devices',
      unreturnedSub: 'across {count} users',
      unreturned: 'Unreturned',
    },
    detail: {
      identity: 'Identity',
      accessScope: 'Access scope',
      lockedTitle: 'Locked — needs supervisor approval',
      lockedBody:
        'This user has {count} unreturned device(s) and can’t check out more until a supervisor reviews — or the devices are returned, marked lost, or recovered.',
      unreturnedDevices: 'Unreturned devices',
      atRisk: 'at risk',
      recentActivity: 'Recent activity · last 14 days',
      noActivity: 'No recent activity in the last 14 days.',
    },
    bulkAccess: {
      button: 'Access',
      header: 'Bulk explicit access',
      scopeHeading: 'Who this applies to',
      scopeSelected: '{count} selected on this page',
      scopeMatching: 'All {count} users matching the current filters',
      modeHeading: 'What to apply',
      modeAllBays: 'All bays',
      modeSpecificBays: 'Specific bays',
      modeRevoke: 'Revoke explicit access',
      allBaysHelp: 'Every bay of every locker, for each of these users.',
      specificBaysHelp: 'Pick lockers and bays once; they are applied to each of these users.',
      revokeHelp:
        'Turns explicit access off. Bay picks are kept, so re-granting restores exactly what they had. Department-derived access is not affected.',
      overwriteWarning:
        'This REPLACES the existing explicit access of {count} users — their current locker and bay picks are overwritten.',
      revokeWarning:
        'This removes explicit access from {count} users. They keep any access their departments grant them.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Why is this access being revoked?',
      reasonHelp: 'Recorded on every affected user’s audit entry.',
      grantConfirm: 'Apply access',
      revokeConfirm: 'Revoke access',
      grantStarted: 'Applying explicit access to {count} users…',
      revokeStarted: 'Revoking explicit access from {count} users…',
    },
    bulkDelete: {
      title: 'Delete users',
      selected: '{count} selected',
      button: 'Delete',
      message:
        'Permanently delete {count} selected user(s)? This removes their access everywhere and cannot be undone.',
      success: 'Deleted {count} user(s)',
      selectAll: 'Select all on page',
      selectRow: 'Select user',
    },
    fingerprint: {
      label: 'Fingerprint',
      enrolled: 'Enrolled',
      enrolledOn: 'Enrolled {date}',
      notEnrolled: 'Not enrolled',
      removed: 'Removed',
      erase: 'Erase',
      eraseTitle: 'Erase fingerprint?',
      eraseMessage:
        "This permanently deletes the member's stored fingerprint from the server and all kiosks. They can re-enrol later.",
      eraseAccept: 'Erase',
    },
    checkoutHold: {
      suspendTitle: 'Suspend checkout',
      suspendMessage:
        'Block this user from checking out more devices? They can still return held devices; a supervisor can re-enable checkout at any time.',
      suspendAccept: 'Suspend checkout',
      reinstateTitle: 'Re-enable checkout',
      reinstateMessage: 'Re-enable checkout for this user?',
      reinstateAccept: 'Re-enable checkout',
      suspended: 'Checkout suspended',
      reinstated: 'Checkout re-enabled',
      suspendedBadge: 'Checkout is suspended for this user',
      suspendedShort: 'Suspended',
      suspendShort: 'Suspend',
      reinstateShort: 'Un-suspend',
      viewDevices: 'View in-flight devices',
      inFlightTip: 'Devices currently checked out (in-flight)',
      detailTitle: 'In-flight devices',
      checkedOutFrom: 'Checked out from',
      lastSeen: 'Last seen',
    },
    rolesPanel: {
      title: 'Roles at the current tenant',
      empty: 'No roles assigned at the current tenant.',
      assignPlaceholder: 'Add a role…',
      initialRole: 'Initial role',
      initialRoleHelp:
        'Assigned immediately after the user is created. Additional roles can be added from the edit view.',
    },
    role: {
      tenantAdmin: 'Tenant Admin',
      manager: 'Manager',
      supervisor: 'Supervisor',
      user: 'User',
      guest: 'Guest',
    },
    guest: {
      title: 'Guest',
      searchPlaceholder: 'Search guests…',
      status: {
        pending: 'Pending',
        approved: 'Approved',
        denied: 'Denied',
      },
      statusFilter: {
        all: 'All statuses',
      },
      counts: {
        total: 'Self-registrations',
        pending: 'Pending',
        approved: 'Approved',
        denied: 'Denied',
      },
      fields: {
        guest: 'Guest',
        badgeId: 'Badge ID',
        sourceLocker: 'Source locker',
        registered: 'Registered',
        assignedDept: 'Assigned dept',
        status: 'Status',
      },
      bulkAction: {
        selected: '{count} selected',
        approveTitle: 'Approve guests',
        approveMessage: 'Approve {count} selected guest(s)? They will be granted user access.',
        approveSuccess: '{count} guest(s) approved',
        denyTitle: 'Deny guests',
        denyMessage: 'Deny {count} selected guest(s)? They will be blocked from checking out.',
        denySuccess: '{count} guest(s) denied',
      },
      registeredFrom: 'Registered from {kiosk}',
      approved: '{name} approved',
      denied: '{name} denied',
      action: {
        approve: 'Approve',
        deny: 'Deny',
      },
      confirm: {
        approveTitle: 'Approve this guest?',
        approveMessage:
          "Approve {name}? This converts them to a regular user and grants access per the tenant's guest policy. The user will be notified if they have web access.",
        denyTitle: 'Deny this guest?',
        denyMessage:
          "Deny {name}? They'll no longer be able to check out devices. If they re-register at a kiosk, the registration goes back to the pending queue.",
      },
      sort: {
        newest: 'Recently registered',
      },
      settings: {
        heading: 'Guest registration',
        description:
          'Controls how self-registrations from the locker are handled for this organization.',
        button: 'Settings',
        saved: 'Settings saved',
        autoApprove: {
          label: 'Auto-approve registrations',
          help: "On: a new registration is approved as soon as it is made. Off: it waits here, or at the locker where it was made, until a supervisor, manager or administrator approves it. Applies to every locker, and can also be changed from a locker's admin screen.",
        },
        defaultGroup: {
          label: 'Default department',
          help: 'Auto-approved users are added to this department on conversion.',
          none: 'No auto-assignment',
        },
      },
      queue: {
        empty: 'No pending registrations.',
        emptySearch: 'No pending registrations match your search.',
        registeredAt: 'Registered {date}',
        bypassed: {
          heading: 'Auto-approval is on',
          body: 'New self-registrations are converted to regular users immediately. The pending queue is bypassed.',
        },
      },
    },
  },
  // ── Resource: Tenant ───────────────────────────────────────────
  tenant: {
    // Shared TenantSelect component — surfaces on every CREATE
    // form a super_admin uses to author tenant-scoped data. Picked
    // value drives the `X-Operating-Tenant` header on the create
    // request. Tenant 0 (platform) is excluded from the list.
    select: {
      placeholder: 'Pick a tenant…',
      searchPlaceholder: 'Search tenants…',
      empty: 'No tenants found.',
      createFor: 'Create for tenant',
      createForHelp:
        'Pick the tenant this new record belongs to. Required because you are super-admin.',
      createForRequired: 'Pick a tenant before saving.',
      // EDIT forms show the owning tenant read-only: it is decided at
      // creation and moving a record between tenants is not an edit.
      ownedBy: 'Tenant',
    },
    // Super-admin tenant catalog + provisioning (platform.tenants.manage).
    title: 'Tenant',
    titlePlural: 'Tenants',
    new: 'Create tenant',
    searchPlaceholder: 'Search tenant name or slug…',
    fields: {
      name: 'Tenant name',
      nameHelp: 'The URL slug is derived from this and cannot be changed later.',
      adminEmail: 'Initial admin email',
      adminEmailHelp: 'Receives the set-your-password invite and becomes the tenant admin.',
      password: 'Admin password',
      passwordHelp:
        'Optional. Leave blank to auto-generate one that nobody is told — the emailed invite is then the only way in. A preset password works immediately, and the invite is still sent.',
    },
    invite: {
      sent: 'Tenant created. Invite sent to {email}.',
      failed: 'Tenant created, but the invite could not be sent: {error}',
      resend: 'Resend invite',
      resent: 'Invite re-sent to {email}.',
      resendFailed: 'Invite could not be sent: {error}',
    },
  },
  // ── Resource: Appliance (On-Prem Clients) ──────────────────────
  // Super-admin catalog of the on-prem servers this cloud manages
  // (platform.appliances.manage).
  mdmProfile: {
    title: 'MDM profile',
    new: 'Add MDM profile',
    empty: 'No MDM profiles yet. A flash station offers MDM enrollment only for a profile listed here.',
    status: {
      enabled: 'Offered',
      disabled: 'Not offered',
    },
    noLockerApp: 'No locker app',
    appInstall: {
      optional: 'The bench decides',
      forbidden: 'Never — the customer’s MDM installs it',
    },
    fields: {
      name: 'Name',
      nameHelp: 'What the flash station shows in its MDM enrollment list.',
      slug: 'Slug',
      slugHelp:
        'Lowercase letters, digits and dashes, e.g. workspace-one-acme. The flash tool and its command line name the profile by it. It cannot be changed later.',
      slugLockedHelp: 'Fixed: flash stations and scripts name the profile by it.',
      vendor: 'MDM product',
      vendorHelp: 'e.g. Workspace ONE.',
      payload: 'Enrollment payload',
      payloadHelp:
        'Paste the content of the customer’s device-owner enrollment QR (JSON). It is stored encrypted and reaches a unit only from the server, never the bench.',
      tokenField: 'Token field',
      tokenFieldHelp:
        'The key the unit hands the typed onboarding token to the MDM agent under (Workspace ONE: gid). Leave empty when the agent asks for the token itself.',
      appInstall: 'Locker app installed by the station',
      appInstallHelp: 'A profile whose customer installs the locker app through their MDM takes the choice away from the bench.',
      enabled: 'Offered to flash stations',
      enabledHelp: 'Switched off, stations stop listing the profile at once. Units already set up with it are not touched.',
      notes: 'Notes',
    },
    errors: {
      payloadJson: 'The payload must be a JSON object, as the enrollment QR carries it.',
      loadFailed: 'The profile could not be read.',
    },
  },
  appliance: {
    title: 'On-prem client',
    titlePlural: 'On-Prem Clients',
    new: 'Add on-prem client',
    empty: 'No on-prem clients yet.',
    status: {
      online: 'Online',
      late: 'Late',
      offline: 'Offline',
      never: 'Never connected',
    },
    fields: {
      name: 'Name',
      nameHelp: 'How this site is known internally, e.g. Acme HQ.',
      customer: 'Customer',
      notes: 'Notes',
      channel: 'Channel',
      serverVersion: 'Server version',
      kioskCount: 'Lockers',
      lastSeen: 'Last seen',
      created: 'Added',
    },
    channelHelp:
      'The channel is this cloud’s own: an on-prem client pulls the APK catalog of the cloud it is registered on.',
    lockers: '{count} lockers',
    detail: {
      identity: 'Identity',
      health: 'Last health report',
      healthNone: 'No report yet — the agent has not checked in.',
      db: 'Database',
      redis: 'Redis',
      stack: 'Server stack',
      diskFree: 'Disk free',
      diskFreeValue: '{gb} GB',
      otaCache: 'Cached APKs',
      reportedAt: 'Reported',
      ok: 'OK',
      down: 'Down',
    },
    tokens: {
      heading: 'Access tokens',
      help: 'The agent on the appliance authenticates to this cloud with one of these. Revoking one ends its access at once.',
      name: 'Token name',
      namePlaceholder: 'e.g. agent',
      nameHint: 'Only a label, to tell this appliance’s tokens apart in the list below.',
      mint: 'Mint token',
      mintedTitle: 'Token minted',
      minted: 'Copy this token now — it is not shown again.',
      token: 'Token “{name}”',
      done: 'Done',
      envLines: 'Put these lines in the appliance’s .env — the channel picks its cloud:',
      install: 'Then restart it and install the agent:',
      copy: 'Copy',
      copied: 'Copied',
      revoke: 'Revoke',
      revokeTitle: 'Revoke token',
      revokeConfirm: 'Revoke {name}? The appliance loses access to this cloud immediately.',
      revokedOk: 'Token revoked.',
      revoked: 'Revoked',
      lastUsed: 'last used {when}',
      neverUsed: 'never used',
      empty: 'No tokens yet.',
    },
    commands: {
      heading: 'Server actions',
      serverUpdate: 'Update server',
      serverUpdateConfirm:
        'Update {name} to the latest server release? The appliance picks this up at its next check-in, within a minute, and restarts its services.',
      refreshCatalog: 'Refresh APK catalog',
      queued: 'Queued — the appliance picks it up at its next check-in.',
      recent: 'Recent actions',
      empty: 'No actions yet.',
      types: {
        server_update: 'Server update',
        refresh_catalog: 'Catalog refresh',
      },
      statuses: {
        pending: 'Pending',
        sent: 'Sent',
        running: 'Running',
        succeeded: 'Succeeded',
        failed: 'Failed',
      },
    },
  },
  // ── Resource: Group ────────────────────────────────────────────
  group: {
    access: {
      heading: 'Department access for members',
      subtitle:
        'The grants above define what this department covers. These actions turn that coverage on (or off) for the department’s members.',
      memberCount: '{count} member | {count} members',
      autoApplyOn: 'New members automatically inherit this department’s bays.',
      applyButton: 'Enable for all {count} members',
      revokeButton: 'Revoke from all members',
      applyHeader: 'Enable department access for {count} members',
      revokeHeader: 'Revoke department access from {count} members',
      applyBody:
        'Every current member will inherit every bay this department is granted. Their other departments and their own explicit access are not affected.',
      revokeBody:
        'Every current member stops inheriting this department’s bays. They stay members, and their other departments and explicit access are untouched.',
      applyNotice: 'Runs in the background for {count} members. You’ll get a notification when it finishes.',
      revokeWarning:
        'This removes department-derived access for {count} members. They will lose the bays this department grants unless another department grants them.',
      autoApplyLabel: 'Automatically apply department access to new members',
      autoApplyHelp:
        'Members added later — by an operator or by a directory sync — inherit this department’s bays without a per-user visit.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Why is this access being revoked?',
      reasonHelp: 'Recorded on every affected member’s audit entry.',
      applyConfirm: 'Enable access',
      revokeConfirm: 'Revoke access',
      applyStarted: 'Enabling department access for {count} members…',
      revokeStarted: 'Revoking department access from {count} members…',
    },

    title: 'Department',
    new: 'Add department',
    searchPlaceholder: 'Search department name, description…',
    userCount: '{count} user | {count} users',
    lockerCount: '{count} locker | {count} lockers',
    fields: {
      name: 'Department name',
      description: 'Description',
      users: 'Users',
      lockers: 'Lockers',
      department: 'Department',
      accessRules: 'Access rules',
      devicesOut: 'Devices out',
      status: 'Status',
    },
    counts: {
      total: 'Total departments',
      usersInDepartments: 'Users in departments',
      accessRules: 'Access rules',
      devicesOut: 'Devices out',
    },
    status: {
      active: 'Active',
      noRules: 'No access rules',
    },
    filter: {
      allStatuses: 'All statuses',
    },
    bulkDelete: {
      title: 'Delete departments',
      selected: '{count} selected',
      button: 'Delete',
      message:
        'Permanently delete {count} selected department(s)? Member assignments + bay grants will be revoked. This cannot be undone.',
      success: 'Deleted {count} department(s)',
      selectAll: 'Select all on page',
      selectRow: 'Select department',
    },
    editorNav: 'Department editor sections',
    detailsHeading: 'Details',
    delete: {
      confirmTitle: 'Delete department?',
      confirmMessage:
        'Delete department "{name}"? Member assignments + bay grants will be revoked. This cannot be undone.',
    },
    members: {
      heading: 'Members',
      description: 'Users assigned to this department. Changes are saved immediately.',
      empty: 'No users in this department yet.',
      emptySearch: 'No users match this search.',
      searchPlaceholder: 'Search name, email, employee ID…',
      addButton: 'Add users',
      removeButton: 'Remove {count}',
      selectedCount: '{count} selected',
      pageOf: 'Page {page} of {total}',
      added: '{count} user added | {count} users added',
      removed: '{count} user removed | {count} users removed',
      accessViaGroups: {
        button: 'Department access',
        enableAll: 'Enable for all members',
        disableAll: 'Disable for all members',
        confirmHeader: 'Department access',
        confirmEnable:
          'Turn on "Grant access using Departments" for every member of this department? Their lockers pick up the change on the next sync.',
        confirmDisable:
          'Turn off "Grant access using Departments" for every member of this department? Group-derived bay access stops on the next locker sync. Memberships are kept.',
        done: 'Updated {changed} of {members} members ({unchanged} already set).',
      },
      addModal: {
        title: 'Add users to {group}',
        searchPlaceholder: 'Search by name, email, employee ID…',
        availableCount: '{count} available',
        empty: 'No users available to add.',
        emptySearch: 'No users match this search.',
        submit: 'Add {count}',
        largeBatchWarning: 'Adding {count} users — this may take a moment.',
      },
    },
    kiosks: {
      heading: 'Locker access',
      description:
        'Define which lockers and bays are available to this department. “All bays” dynamically includes future bays within the selected locker.',
    },
  },
  // ── Resource: Device + DeviceModel ─────────────────────────────
  deviceModel: {
    title: 'Device model',
    titlePlural: 'Device models',
    new: 'Add model',
    edit: 'Edit model',
    searchPlaceholder: 'Search model, vendor, notes…',
    noVendor: 'No vendor',
    fields: {
      model: 'Model',
      vendor: 'Vendor',
      chargable: 'Chargeable',
      chargingMode: 'Charging',
      unitCost: 'Unit cost',
      unitCostHelp: 'Per-unit cost; snapshotted onto each device at creation for loss accounting.',
      notes: 'Notes',
    },
    chargingModes: {
      charging: 'Charging',
      chargingHelp: 'Charge until the device is full, then cut USB power (the locker\'s smart charging settings apply).',
      non_charging: 'Non-charging',
      non_chargingHelp: 'USB power stays off — for items that don\'t charge.',
      bypass: 'Bypass',
      bypassHelp: 'Smart charging off for this model: USB power stays on while the device is in the bay, with no detection.',
    },
  },
  assetAccounting: {
    tabs: {
      overview: 'Overview',
      loss: 'Loss & damage',
      inventory: 'Inventory value',
      repair: 'Repair tracker',
      rules: 'Thresholds & rules',
    },
  },
  device: {
    title: 'Device',
    counts: {
      total: 'Total',
      fleetValue: 'Fleet value',
      inUse: 'In use',
      overdue: 'Overdue',
      damaged: 'Damaged',
      retiredSub: 'out of service',
      deletedSub: 'restorable',
    },
    new: 'Add device',
    edit: 'Edit device',
    recovered: 'Recovered',
    reactivate: 'Reactivate',
    reinstate: 'Reinstate',
    restore: 'Restore',
    recoveredSuccess: 'Device recovered',
    reactivateSuccess: 'Device reactivated',
    reinstateSuccess: 'Device reinstated',
    restoreSuccess: 'Device restored',
    delete: {
      title: 'Delete device',
      confirmSingle: 'Delete "{name}"? This can be undone by an administrator.',
      confirmBulk: 'Delete {count} device(s)? This can be undone by an administrator.',
      reasonLabel: 'Reason for deletion',
      reasonPlaceholder: 'Select a reason',
      noReasons: 'No reasons configured. Add some under Settings → Device deletion reasons.',
      otherDetailLabel: 'Describe the reason',
      otherDetailPlaceholder: 'e.g. Returned damaged after field use…',
    },
    deleted: {
      reason: 'Reason',
      by: 'Deleted by',
      at: 'Deleted at',
    },
    stateLocked: 'Locked while lost / damaged / retired — recover it first',
    lockReason: {
      state:
        'Locked — this device is {state}. Use the recovery action below to return it to Active before editing.',
      inFlightOrder:
        'Locked — this device has an in-flight order. Editing is blocked until the order is returned.',
      deleted: 'This device is deleted — shown read-only. Restore it to make changes.',
    },
    manageModels: 'Manage models',
    searchPlaceholder: 'Search name, SN, NFC, RFID, notes…',
    viewMode: 'View mode',
    unassigned: 'Unassigned',
    unassignedModel: 'Unassigned',
    charge: {
      title: 'Charging (last hour)',
      voltage: 'Voltage (V)',
      current: 'Current (A)',
      power: 'Power (W)',
      empty: 'No recent charge data for this bay.',
    },
    chargeHistory: {
      title: 'Charge history (last 10 sessions)',
      subtitle: 'Each metric is scaled to its own peak across these sessions.',
      time: 'Time',
      energy: 'Energy',
      current: 'Avg current',
      voltage: 'Avg voltage',
      empty: 'No charge sessions recorded for this device yet.',
    },
    recentCheckouts: {
      title: 'Recent checkouts (last 5)',
      empty: 'No checkout history for this device yet.',
      unknownUser: 'Unknown user',
      bay: 'Bay {number}',
    },
    detail: {
      identity: 'Identity',
      currentlyWith: 'Currently with',
      location: 'Location',
      chargeHistory: 'Charge history',
      recentCheckouts: 'Recent checkouts',
      movementHistory: 'Movement history',
      held: 'Held',
    },
    movements: {
      empty: 'No movements recorded yet.',
      heldBy: 'held by {name}',
      by: 'by {name}',
      events: {
        OPENING_BALANCE: 'Opening balance',
        ADDED: 'Added',
        ASSIGNED_TO_BAY: 'Assigned to a bay',
        REMOVED_FROM_BAY: 'Removed from its locker',
        TRANSFERRED: 'Transferred',
        CHECKED_OUT: 'Checked out',
        RETURNED: 'Returned',
        REPORTED_DAMAGED: 'Reported damaged',
        REPAIRED: 'Repaired',
        REPORTED_LOST: 'Reported lost',
        LOST_CONFIRMED: 'Loss confirmed',
        RECOVERED: 'Recovered',
        RETIRED: 'Retired',
        REINSTATED: 'Reinstated',
        REMOVED: 'Deleted',
        RESTORED: 'Restored',
        CONDITION_CHANGED: 'Condition changed',
      },
    },
    lifecycle: {
      reason: 'Reason',
      locker: 'Destination locker',
      bay: 'Destination bay',
      transfer: {
        action: 'Transfer',
        title: 'Transfer device',
        hint: 'Move this device to another bay. It stays in inventory; the move and its reason are recorded.',
      },
      remove: {
        action: 'Remove from locker',
        title: 'Remove from locker',
        hint: 'Take this device out of its bay. It stays owned and in service, unstocked, until it is assigned again.',
      },
      retire: {
        action: 'Retire',
        title: 'Retire device',
        hint: 'Retire this device (end of life, trade-in, beyond repair). It leaves its bay and active inventory; its history is kept.',
      },
    },
    emptyState: {
      heading: 'No device models yet',
      description:
        'Devices reference a model from your catalog. Add your first device model to get started.',
      cta: 'Add device model',
    },
    filter: {
      allModels: 'All models',
      allStates: 'All states',
      allLockers: 'All lockers',
      allLocations: 'All locations',
      allDepartments: 'All departments',
      chargable: 'Chargeable only',
      chargableHint: 'Show only devices that need charging.',
    },
    fields: {
      name: 'Name',
      serialNumber: 'Serial number',
      deviceModel: 'Model',
      deviceModelPlaceholder: 'Type to search models…',
      state: 'State',
      locker: 'Locker',
      location: 'Location',
      created: 'Created',
      device: 'Device',
      status: 'Status',
      locationDepartment: 'Location · Department',
      lockerBay: 'Locker · Bay',
      department: 'Department',
      user: 'User',
      replacement: 'Value',
      lastUpdate: 'Last update',
      nfcTag: 'NFC tag',
      rfidTag: 'RFID tag',
      tags: 'Tags',
      unitCostSnapshot: 'Unit cost (at creation)',
      unitCostSnapshotHelp:
        'Per-unit cost frozen from the model when this device was created. Immutable.',
      notes: 'Notes',
      bayAssignment: 'Locker & bay',
      lockerPlaceholder: 'Select a locker…',
      bay: 'Bay',
      bayPlaceholder: 'Select a bay…',
      baySelectLockerFirst: 'Select a locker first',
      devicesInBay: 'Already in this bay',
      bayAssignmentHelp:
        'Assign this device to a bay. Changing the bay moves it; clear the locker or bay to remove it.',
      bayInFlightLocked:
        "This device's bay has an in-flight order — its assignment is locked until the order closes.",
    },
    status: {
      available: 'Available',
      inUse: 'In use',
      overdue: 'Overdue',
    },
    state: {
      active: 'Active',
      lost: 'Lost',
      retired: 'Retired',
      damaged: 'Damaged',
      missing_parts: 'Missing parts',
      not_charging: 'Not charging',
      abnormal: 'Abnormal',
      // Synthetic State-dropdown option — selects the soft-deleted view.
      deleted: 'Deleted',
    },
    bulkDelete: {
      selected: '{count} selected',
      selectAll: 'Select all',
      button: 'Delete',
      clear: 'Clear',
      confirmTitle: 'Delete {count} device(s)?',
      confirmMessage:
        'The selected devices will be removed. This can be undone by an administrator.',
      success: '{count} device(s) deleted.',
    },
  },
  // ── Resource: Kiosk + commands ─────────────────────────────────
  kiosk: {
    // Retired-locker lifecycle (Lockers list "Deleted" view).
    deleted: {
      toggle: 'Deleted',
      tooltip: 'Show retired lockers',
    },
    restore: {
      tooltip: 'Restore this locker',
      confirmHeader: 'Restore locker?',
      confirmBody:
        'Bays and their user/department assignments come back. Bay states and device-to-bay assignments were removed permanently when it was deleted, so devices must be re-assigned and each bay reports its own state once the unit reconnects.',
      accept: 'Restore',
      done: 'Locker restored with {bays} bay(s)',
      doneAwaitingState: 'Locker restored with {bays} bay(s); {awaiting} awaiting state from the unit',
    },
    purge: {
      tooltip: 'Delete permanently',
      confirmHeader: 'Permanently delete {name}?',
      confirmBody:
        'This removes the locker, its bays, and ALL of its orders, transactions and history. It cannot be undone and cannot be restored.',
      accept: 'Delete permanently',
      done: '{name} permanently deleted',
    },
    title: 'Locker',
    titlePlural: 'Lockers',
    detailsHeading: 'Locker details',
    detailsDescription: 'Identity, location, and version metadata.',
    assignmentHeading: 'Bay assignment',
    assignmentDescription: 'Assign devices to bays. Bays with an active checkout are locked.',
    editorNav: 'Editor sections',
    loadPresets: 'Load template',
    syncConfigNow: 'Sync now',
    syncConfigNowToast: 'Config sync triggered',
    syncConfigNowError: 'Failed to trigger config sync',
    presetsEmpty: 'No presets defined',
    bayCountReduceBlocked:
      'Cannot set bay count to {attempted} — bay #{min} still holds a device. Empty the bays above {attempted} first.',
    bayCountMinHelp: 'Minimum {min} bays.',
    unwiredChannelsHelp:
      'Lock-board ports with no door wired, counted straight through the chain (board 4 port 7 = 31). Bay numbers skip them so they keep matching the door labels.',
    statusHelp:
      'Maintenance and Decommissioned put the locker out of service: it shows "Out of service" and refuses new check-outs and deposits, while returns and pickups still work.',
    config: {
      heading: 'Configuration',
      description:
        'Behavior settings for check-in, checkout, operation, and self-registration. Use "Load template" to copy from a saved template; changes here only apply to this locker.',
    },
    bayConfig: {
      heading: 'Bay Details',
      description:
        'Per-bay device assignment, operational state, and enable/disable. Saves with the locker.',
      empty: 'No bays on this locker.',
      bayLabel: 'Bay {number}',
      inFlightLockHint:
        'This bay has an in-flight order — its mode and devices are locked until the order completes.',
      checkedOutBy: 'Currently checked out by',
      multiCheckoutViolation: 'Multi-checkout violation (server)',
      checkedOutDevices: 'Assigned devices',
      settings: 'Settings',
      settingsHeader: 'Bay {number} settings',
      deviceLabel: 'Devices',
      devicesAssigned: 'Devices assigned',
      devicePlaceholder: 'Pick devices…',
      acNoAssignment: 'AC bays hold no device — nothing to assign.',
      inFlightOrder: 'In-flight order',
      updateAssignments: 'Update',
      resetAssignments: 'Reset',
      pendingAssignments: '{count} pending',
      stateLabel: 'Operational state',
      modeLabel: 'Bay mode',
      mode: {
        AC: 'AC — Anonymous',
        DC: 'DC — Designated',
      },
      inventoryLabel: 'Inventory bay',
      inventoryHelp:
        'Storage only: keeps its assigned devices but runs no checkout journey — no stock/restock, no check-in or condition report, and charging and UVC stay off. Never auto-allocated; an assigned user with "Selectable bays" opens it by hand, and only the door open/close is logged.',
      enabledLabel: 'Enabled',
      enabledOn: 'In service',
      enabledOff: 'Disabled',
      resetLabel: 'Reset',
      resetHeader: 'Reset bay {number}',
      resetBody:
        'Closes any open order on this bay (logging your reason) and sets it to Available. The reset is sent to the locker.',
      resetReasonLabel: 'Reason',
      resetReasonPlaceholder: 'Why are you resetting this bay?',
      resetConfirm: 'Reset bay',
      resetIssued: 'Reset sent to bay {number}',
      resetConfirmed: 'Bay {number} reset — now available',
      resetTimeout: 'Bay {number} reset sent, but no confirmation yet',
      resetFailed: 'Failed to reset bay {number}',
      selectBay: 'Select bay {number}',
      selectAllBays: 'Select all bays',
      baysSelected: '{count} selected',
      clearSelection: 'Clear',
      resetSelected: 'Reset {count} bays',
      bulkResetHeader: 'Reset {count} bays',
      bulkResetBody:
        'Closes any open order on each selected bay (logging your reason) and sets it to Available. Commands are sent one per second to stay inside the locker command rate limit, so a large batch takes a minute or two.',
      bulkResetOpenOrders:
        '{count} selected bay(s) hold an in-flight order that will be force-closed: {bays}',
      bulkResetConfirm: 'Reset {count} bays',
      bulkResetIssuing: 'Sending resets… {done} of {total}',
      bulkResetConfirming: 'Waiting for the locker to confirm… {done} of {total}',
      bulkResetStop: 'Stop',
      bulkResetAllConfirmed: 'All {total} bays reset — now available',
      bulkResetSummary:
        '{confirmed} of {total} bays confirmed · {failed} failed · {unconfirmed} unconfirmed',
      bulkResetOutcome: {
        ack: 'Confirmed by the locker',
        fail: 'The locker rejected the reset',
        timeout: 'Sent, but not confirmed yet',
        error: 'The command could not be sent',
      },
      resolveLabel: 'Resolve',
      resolveHeader: 'Resolve bay {number}',
      resolveBody:
        'Resolve each suspending device to clear this bay. The locker releases it for restock once all are resolved.',
      resolveDeviceLabel: 'Device',
      resolutionLabel: 'Resolution',
      resolutionPlaceholder: 'Choose a resolution',
      replacementLabel: 'Replacement device',
      replacementPlaceholder: 'Choose an unassigned device',
      resolveNoteLabel: 'Note',
      resolveNotePlaceholder: 'Why / what was done (required)',
      resolveConfirm: 'Resolve',
      resolveSuccess: 'Resolution applied',
      bayTitle: 'Bay {number}',
      currentState: 'Current state',
      employeeId: 'Employee ID',
      personalId: 'Personal ID',
      hardware: 'Hardware',
      currentDraw: 'Current draw',
      power: 'Power',
      uvcLamp: 'UVC sanitization lamp',
      usbPower: 'USB charging power',
      chargingLastHour: 'Charging · last hour',
      editConfiguration: 'Edit configuration',
      openBay: 'Open bay',
      checkedOutAgo: 'Checked out {ago}',
      openBaySuccess: 'Bay {number} opened',
      openBayFailed: 'Could not open bay {number}',
      openBayTimeout: 'No confirmation from bay {number}',
      state: {
        INITIAL: 'Newly provisioned',
        AVAILABLE: 'Available',
        CHECKED_OUT: 'Checked out',
        CHECKED_IN: 'Checked in',
        CHARGING: 'Charging',
        FULL_CHARGE: 'Fully charged',
        DEACTIVATED: 'Deactivated',
        CLEANING: 'Cleaning',
        SUSPENDED: 'Suspended',
        NOT_CONNECTED: 'Device not connected',
        PENDING_STOCK: 'Pending stock',
        DELAYED: 'Overdue',
        MAINTENANCE: 'Maintenance',
        RESERVED: 'Reserved',
        OCCUPIED: 'Occupied',
      },
    },
    online: 'Online',
    offline: 'Offline',
    new: 'Add locker',
    control: {
      heading: 'Control',
      description: 'Run diagnostics and toggle hardware features on this locker.',
      commandAcked: '{command}: ack received',
      commandFailed: '{command}: failed on the locker',
      commandTimeout: '{command}: no ack within 30s',
      commandIssueFailed: 'Failed to issue {command}',
      targetHeading: 'Target',
      targetDescription: 'Which bay subsequent actions apply to.',
      targetAllBays: 'All bays',
      targetBayN: 'Bay {bay}',
      capabilitiesHeading: 'Hardware capabilities',
      capabilitiesDescription: 'Enable or disable the hardware features available on this locker.',
      capabilities: {
        rfid: 'RFID scanner',
        rfidHelp: 'Allows the locker to read RFID badges.',
        nfc: 'NFC scanner',
        nfcHelp: 'Allows the locker to read NFC badges and phones.',
        usbScanner: 'USB scanner (reserved)',
        usbScannerHelp: 'Reserved for future USB scanner support. Not currently available.',
        ota: 'Software updates',
        otaHelp: 'Download firmware and app updates automatically.',
      },
      dataHeading: 'Data sync',
      dataDescription: 'Refresh all locker records from the server.',
      reconcile: 'Reconcile now',
      reconcileHelp: 'Force a complete data resynchronization.',
      reconcileAction: 'Reconcile',
      actuatorsHeading: 'Manual bay actions',
      actuatorsDescription:
        'Directly control bay hardware for diagnostics, testing, and one-off recovery actions.',
      unlock: 'Unlock bay',
      unlockAction: 'Unlock',
      unlockHelpScope: 'Pulse the lock actuator on {target}.',
      usbPower: 'USB power',
      usbPowerHelp: 'Switch the bay’s USB charging output on or off.',
      uvcLight: 'UVC sanitization light',
      uvcLightHelp: 'Switch the bay’s UV-C sanitizing lamp on or off.',
      led: {
        heading: 'LED',
        help: 'Toggle, animation mode, color, and intensity. Pick a system swatch or set a custom color.',
        permanenceNotice: 'Immediately overrides the automatic LED state.',
        apply: 'Apply LED settings',
        previewLabel: 'LED preview',
        power: 'Toggle',
        powerHelp: 'Master on/off for the bay LED. Mode + color + intensity all gate on this.',
        mode: 'Mode',
        modeSolid: 'Solid',
        modeBlink: 'Blink',
        modeBreathe: 'Breathe',
        modeRainbow: 'Rainbow',
        modeRainbowBreathe: 'Rainbow breathe',
        customColor: 'Custom',
        color: 'Color',
        intensity: 'Intensity ({value}%)',
        systemSwatches: 'System bay-state swatches',
      },
    },
    stats: {
      heading: 'Stats',
      description: 'Connectivity status and live per-bay sensor readings.',
      notAvailableInCreate: 'Stats become available after the locker is created.',
      connectivityHeading: 'Connectivity',
      status: 'Status',
      hardwareHeading: 'Hardware health',
      hardwareNoData: "No hardware-stats sample yet — locker hasn't reported.",
      hardwareFetchFailed: 'Failed to fetch hardware stats',
      uptime: 'Uptime',
      freeMemory: 'Free memory',
      pollingRtt: 'Polling RTT',
      pollingSuccess: 'Polling success',
      dispatcherLag: 'Dispatcher lag',
      sampledAt: 'Sampled',
      bayTelemetryHeading: 'Per-bay telemetry',
      bayTelemetryDescription: 'Real-time hardware status and sensor readings.',
      placeholderNotice: 'Telemetry stream not yet wired — placeholder values.',
      bayTelemetryWaitingNotice: 'Waiting for first sensor sample from the locker (≤30s).',
      bay: 'Bay',
      door: 'Door',
      uvc: 'UVC',
      usb: 'USB',
      connected: 'Connected',
      charging: 'Charging',
      voltage: 'Voltage',
      amperage: 'Amperage',
      wattage: 'Wattage',
      doorOpen: 'Open',
      doorClosed: 'Closed',
      on: 'On',
      off: 'Off',
    },
    listSubtitle: 'Physical locker units. Send commands via the editor.',
    searchPlaceholder: 'Search by name, serial, location or dept…',
    viewMode: 'View mode',
    health: {
      online: 'Online',
      hasIssues: 'Has issues',
      offline: 'Offline',
    },
    modelBay: '{count}-bay',
    modelBayFull: '{count}-bay model',
    showingLockers: 'Showing {totalRecords} lockers',
    clearFilters: 'Clear filters ({count})',
    filter: {
      allStates: 'All statuses',
      bayCount: 'Bay count',
      location: 'Location',
      locationCount: '{count} locations',
      department: 'Department',
    },
    tabs: {
      kiosks: 'Lockers',
      locations: 'Locations',
      templates: 'Templates',
    },
    loadTemplate: 'Load template',
    loadTemplatePicker: 'Pick a template to fill the configuration block.',
    loadTemplateConfirm: 'Load template "{name}"?',
    loadTemplateConfirmMessage:
      'This will overwrite the current bay count and per-bay defaults. Continue?',
    unassignedLocation: 'No location',
    fields: {
      health: 'Health',
      locker: 'Locker',
      modelBays: 'Model · Bays',
      department: 'Department',
      serialNumber: 'Serial number',
      name: 'Display name',
      location: 'Location',
      locationPlaceholder: 'Pick a location…',
      folder: 'Folder',
      folderPlaceholder: 'Not specified',
      bayCount: 'Bay count',
      unwiredChannels: 'Unwired ports',
      status: 'Status',
      firmwareVersion: 'Firmware',
      appVersion: 'App version',
      lastSeen: 'Last seen',
      lastIp: 'Last IP',
      notes: 'Notes',
    },
    status: {
      active: 'Active',
      maintenance: 'Maintenance',
      decommissioned: 'Decommissioned',
    },
    commands: {
      title: 'Issue command',
      subtitle: 'Commands are published over MQTT. The locker ACKs asynchronously.',
      command: 'Command',
      bayNumber: 'Bay number',
      submit: 'Send command',
      unlock: 'Unlock bay',
      lock: 'Lock bay',
      refreshConfig: 'Refresh config',
    },
    lastSeenAgo: 'Last seen {ago}',
    neverSeen: 'Never connected',
    detail: {
      identity: 'Identity',
      bayState: 'Bay state · live',
      software: 'Software',
      recentEvents: 'Recent events',
      noEvents: 'No recent events for this locker.',
      model: 'Model',
      lastReported: 'Last reported',
      baysAvailable: '{count} available',
      baysIssues: '{count} issues',
      baysTotal: '{count} bays',
      syncNow: 'Sync now',
      syncStarted: 'Sync command dispatched.',
      rearmOnboarding: 'Re-arm onboarding',
      rearmDone: 'Onboarding window re-armed. The locker adopts it on its next retry.',
    },
  },
  // ── Org tree (tag/folder hierarchy nav) ────────────────────────
  tagTree: {
    // Platform-tier operators only: the folder tree belongs to a tenant,
    // so authoring in it needs a tenant chosen in the toolbar filter.
    pickTenantToManage: 'Pick a tenant to manage folders.',
    title: 'Locations',
    collapse: 'Hide locations',
    expand: 'Show locations',
    add: 'Add folder',
    rename: 'Rename',
    move: 'Move',
    remove: 'Remove',
    namePlaceholder: 'Folder name',
    added: 'Folder added',
    renamed: 'Folder renamed',
    moved: 'Folder moved',
    removed: 'Folder removed',
    moveTitle: 'Move folder',
    moveHelp: 'Choose a new parent for "{name}".',
    moveDestination: 'New parent folder',
    removeTitle: 'Remove folder',
    removeMessage:
      'Remove "{name}"? Its items are kept but unfiled, and any sub-folders move up one level.',
  },
  // ── Resource: Location ─────────────────────────────────────────
  location: {
    title: 'Location',
    titlePlural: 'Locations',
    new: 'Add location',
    edit: 'Edit location',
    searchPlaceholder: 'Search name, address, city…',
    geoLabel: 'Geo coordinates',
    filter: {
      hasLockers: 'Has lockers',
    },
    counts: {
      lockers: 'locker | lockers',
      devices: 'device | devices',
    },
    fields: {
      name: 'Name',
      folder: 'Hierarchy',
      folderPlaceholder: 'Not specified',
      address: 'Address',
      city: 'City',
      state: 'State / region',
      zip: 'ZIP / postal',
      latitude: 'Latitude',
      longitude: 'Longitude',
      notes: 'Notes',
    },
  },
  // ── Resource: KioskTemplate ────────────────────────────────────
  kioskTemplate: {
    title: 'Locker template',
    titlePlural: 'Locker templates',
    new: 'Add template',
    edit: 'Edit template',
    searchPlaceholder: 'Search name, description…',
    bayCountLabel: '{count} bays',
    defaultBadge: 'Default',
    sections: {
      // Top-level panel groups. Match the locker-side admin
      // ConfigSection's `AdminAccordionGroup` titles so the
      // operator's mental model is consistent across server-side
      // template/per-kiosk editors and the kiosk-side admin Config.
      details: 'Template details',
      modeAndGeneral: 'Mode & general',
      smartCharging: 'Smart charging',
      branding: 'Branding',
      designatedModeFlows: 'Designated mode Config',
      anonymousModeFlows: 'Anonymous mode flows',
      scanner: 'Scanner',
    },
    subsections: {
      // Sub-blocks within a section: `Mode & general` → Scanner
      // preprocessing; `Designated mode Config` → the rest. Locker uses
      // BlockCard wrappers; the FE here uses plain divs.
      scannerPreprocessing: 'Scanner Preprocessing',
      checkIn: 'Check-in',
      checkout: 'Checkout',
      flow: 'Flow',
      selfRegistration: 'Self-registration',
    },
    fields: {
      name: 'Name',
      description: 'Description',
      bayCount: 'Bay count',
      isDefault: 'Default template',
      isDefaultHelp:
        'Set this template as the tenant default. The Kiosk editor preloads it when creating a new kiosk. Marking another template as default automatically clears this one.',
      // Late-qualification label morphs by operation.mode so the
      // operator sees the wording that matches what the watchdog
      // actually flags (return-overdues in direct_assignment,
      // pickup-overdues in anonymous, neutral in hybrid). Matches
      // locker-side GeneralBlock's mode-aware label.
      lateQualificationDirectAssignment: 'Flag overdue returns',
      lateQualificationAnonymous: 'Flag overdue returns',
      lateQualificationHybrid: 'Flag overdue returns',
      lateQualificationEnabledHelp:
        'Mark a checkout overdue when it goes longer than the threshold below.',
      lateQualificationThreshold: 'Overdue threshold',
      identificationMethod: 'Identification method',
      identificationDevice: 'Device ID',
      identificationUser: 'User ID',
      identificationBoth: 'Both',
      identificationRequestPin: 'Request user PIN',
      identificationRequestPinHelp: 'Prompt the user for a PIN as part of the check-in flow.',
      conditionReportEnabled: 'Device condition report',
      conditionReportEnabledHelp:
        'Ask for each returned device\'s condition. When off, a device scanned at return is recorded as good and there is no condition step.',
      userIdRegexExtract: 'User ID regex extract',
      userIdRegexExtractHelp:
        'Check-in and checkout: a scan used as a user ID is matched, and reported, as this pattern\'s first capture group or its whole match; a scan the pattern doesn\'t match is used as scanned. Not applied while the Overall Regex extract is set. Leave blank for off.',
      deviceIdRegexExtract: 'Device ID regex extract',
      deviceIdRegexExtractHelp:
        'Check-in and restock: a scan used as a device ID is matched, and reported, as this pattern\'s first capture group or its whole match; a scan the pattern doesn\'t match is used as scanned. Not applied while the Overall Regex extract is set. Leave blank for off.',
      scannerRegexExtract: 'Overall Regex extract',
      scannerRegexExtractHelp:
        'Applied to every scan from every scanner on the locker, USB and serial, that is used as a user ID or a device ID. A scan becomes the first capture group, or the whole match; a scan the pattern doesn\'t match passes unchanged. While set, the User ID and Device ID regex extracts are not applied. The locker\'s admin fields always take a scan as read. Leave blank for off.',
      smartChargingEnabled: 'Smart charging',
      smartChargingEnabledHelp:
        'Detect when a device is full and cut its USB power. When off, USB power stays on while a device is in the bay, with no detection.',
      maintainCharge: 'Maintain charge',
      maintainChargeHelp:
        'After a device is full, charge it again once the wait below has passed, and repeat. The bay stays available while it tops up.',
      maintainChargeWait: 'Wait before charging again',
      maintainChargeWaitHelp: 'Time between a full charge and the next top-up.',
      checkoutRequestPin: 'Request user PIN on checkout',
      doorOpenTimeout: 'Door open timeout',
      doorOpenTimeoutHelp:
        'How long the kiosk waits after a door opens before flagging the bay as still open.',
      actionFlowIdleTimeout: 'Action flow idle timeout',
      actionFlowIdleTimeoutHelp:
        'Per-step idle limit during check-in / check-out. When the operator is idle this long the flow self-cancels and returns to home.',
      uvcKickInDelay: 'UVC kick-in after',
      uvcKickInDelayHelp: 'Delay after door close before UVC cleaning starts.',
      cleaningDuration: 'Cleaning cycle duration',
      cleaningDurationHelp:
        'How long the UVC sanitization cycle runs after the kick-in delay elapses. The bay sits in CLEANING for this duration.',
      maxChargingTime: 'Max charging time',
      maxChargingTimeHelp:
        'Safety ceiling for how long a bay stays in CHARGING state after cleaning completes (direct-assignment mode). On elapse the bay transitions to AVAILABLE.',
      screensaverEnabled: 'Screensaver',
      screensaverEnabledHelp: 'Show a screensaver when the locker is idle.',
      screensaverIdleTimeout: 'Screensaver idle after',
      screensaverIdleTimeoutHelp:
        'Idle window before the screensaver overlay engages on the customer home screen. Ignored when the screensaver is off.',
      brandingSlotsHelp:
        'Per-surface display mode. Off hides the surface; Default uses the built-in asset; Custom uses the server-supplied image/video (built-in until one is pushed). Assets are pushed from the server — there is no upload here.',
      brandingModeOff: 'Off',
      brandingModeDefault: 'Default',
      brandingModeCustom: 'Custom',
      brandingSlotSplash: 'Splash screen',
      brandingSlotScreensaver: 'Screensaver',
      brandingSlotAcCheckin: 'Deposit button (Access-code)',
      brandingSlotAcCheckout: 'Pickup button (Access-code)',
      brandingSlotDcCheckin: 'Check-in button (Device-scan)',
      brandingSlotDcCheckout: 'Check-out button (Device-scan)',
      brandingSlotHybridPublic: 'Mode selector — Anonymous',
      brandingSlotHybridAssigned: 'Mode selector — Direct assignment',
      brandingSlotDeviceSample: 'Device sample',
      brandingSlotScanSample: 'Scan sample',
      returnFlow: 'Return flow order',
      returnFlowDeviceFirst: 'Device first',
      returnFlowUserFirst: 'User first',
      operationMode: 'Operation mode',
      operationModeHelp:
        'Direct assignment: a specific user is granted a specific bay. Anonymous: a 6-digit passcode is issued at check-in for device retrieval. Hybrid: both workflows active, partitioned per bay.',
      operationModeDirectAssignment: 'Direct assignment',
      operationModeAnonymous: 'Anonymous',
      operationModeHybrid: 'Hybrid',
      passiveRestockEnabled: 'Auto-open bay on device scan',
      passiveRestockEnabledHelp:
        'When on, scanning a device on the home screen opens its assigned bay so the operator can place it. Only applies to direct-assignment and hybrid modes.',
      passiveCheckInEnabled: 'Auto-start return on device scan',
      passiveCheckInEnabledHelp:
        'When on, scanning a checked-out device on the home screen auto-starts the check-in flow with the device pre-loaded. Only applies to direct-assignment and hybrid modes.',
      passiveCheckoutEnabled: 'Auto-start checkout on badge scan',
      passiveCheckoutEnabledHelp:
        'When on, scanning a member ID (Personal ID or Employee ID) on the home screen starts checkout — or a checkout/return prompt if they already hold a bay. Only applies to direct-assignment and hybrid modes.',
      acIdentificationMethod: 'Identification method',
      acIdentificationMethodHelp:
        'How a user is identified at anonymous-mode check-in. PIN only: passcode is the sole anchor. User auth: user ID + stored PIN required before issuing a passcode, except with ID to Pin, where the ID itself is the passcode and no user is checked.',
      acIdentificationPinOnly: 'PIN only',
      acIdentificationUserAuth: 'User ID + PIN',
      acPinSource: 'Passcode source',
      acPinSourceHelp:
        "Where the locker passcode comes from. System generated: kiosk mints a 6-digit code on success. User entered: operator types it (adds a passcode-entry step). ID to Pin (User auth only): the person's ID is the passcode, with no user check and no PIN; anyone with the ID can open the locker.",
      acPinSourceSystemGenerated: 'System-generated',
      acPinSourceUserEntered: 'User-entered',
      acPinSourceIdAsPin: 'ID to Pin',
      selfRegistrationEnabled: 'Enable self-registration',
      selfRegistrationEnabledHelp:
        'Let new users register themselves on the kiosk without operator assistance. Whether a registration is approved at once or waits for a decision is set for the whole organization under Guests > Settings.',
      notes: 'Notes',
    },
  },
  // ── Resource: Order ────────────────────────────────────────────
  order: {
    title: 'Order',
    fields: {
      id: 'Order #',
      kiosk: 'Locker',
      bay: 'Bay',
      user: 'User',
      status: 'Status',
      openedAt: 'Opened',
      closedAt: 'Closed',
      notes: 'Notes',
    },
    status: {
      open: 'Open',
      closed: 'Closed',
      cancelled: 'Cancelled',
      expired: 'Expired',
      error: 'Error',
      delayed: 'Overdue',
      orphaned: 'Orphaned',
    },
    detail: {
      title: 'Order {id}',
      itemsTab: 'Items',
      accessTab: 'Access',
      transactionsTab: 'Transactions',
    },
    transactionKind: {
      checkout: 'Check out',
      checkin: 'Check in',
      cancel: 'Cancelled',
      admin_override: 'Admin override',
      alarm: 'Alarm',
      error: 'Error',
    },
  },
  // ── Resource: Access Matrix (axis-filter + perspective view) ───
  accessMatrix: {
    title: 'Access matrix',
    subtitle:
      'See who can open which bays. Filter by user, department, locker, bay, or master key. Switch perspectives to view the data from different angles.',
    filtersAriaLabel: 'Access matrix filters',
    perspectiveAriaLabel: 'Result perspective',
    clearFilters: 'Clear filters',
    bayLabel: 'Bay {number}',
    bayCount: '{count} bay | {count} bays',
    edgeCount: '{count} grant | {count} grants',
    openKey: '(open key — no assignee)',
    empty: 'No matching access records. Clear filters or broaden your selection.',
    prompt: {
      title: 'Pick a filter to load the matrix',
      body: 'Use the filters above to narrow the matrix to a specific user, department, locker, bay, or master key.',
    },
    summary:
      '{edge_count} access grants · {user_count} users · {kiosk_count} lockers · {bay_count} bays',
    filters: {
      user: 'User…',
      group: 'Department…',
      kiosk: 'Locker…',
      bay: 'Bay…',
      masterKey: 'Master key…',
    },
    perspective: {
      byKioskBay: 'By locker → bay',
      byUser: 'By user',
      byGroup: 'By department',
      byPath: 'By grant path',
    },
    path: {
      directAll: 'direct (grant-all)',
      directBay: 'direct',
      viaGroup: 'via department "{name}"',
      viaMasterKey: 'via master key "{name}"',
      viaPrefix: 'via',
    },
    pathBucket: {
      DIRECT_ALL: 'Direct grants (all bays)',
      DIRECT_BAY: 'Direct bay assignments',
      GROUP: 'Department inheritance',
      MASTER_KEY: 'Master keys',
    },
    viewModeAriaLabel: 'Switch between list and graph view',
    graph: {
      legend: 'Legend',
      nodeUser: 'User',
      nodeGroup: 'Department',
      nodeKey: 'Master key',
      nodeBay: 'Bay',
      edgeDirect: 'Direct grant',
      edgeGroup: 'Department inheritance',
      edgeKey: 'Master-key grant',
    },
  },
  // ── File uploader component ─────────────────────────────────────
  fileUploader: {
    choose: 'Choose files',
    upload: 'Upload',
    clear: 'Clear',
    dropPrompt: 'Drag files here',
    orClickToBrowse: 'or click to browse',
    uploadingNFiles: 'Uploading {count} file | Uploading {count} files',
    allUploaded: 'Upload complete',
    status: {
      queued: 'Queued',
      uploading: 'Uploading',
      success: 'Uploaded',
      error: 'Failed',
    },
  },
  // ── Resource: Bay Access (LEGACY — kept for transitional compatibility) ───
  bayAccess: {
    title: 'Bay Access',
    subtitle: 'Manage user grants, department grants, and master keys.',
    tabs: {
      userGrants: 'User grants',
      groupGrants: 'Department grants',
      masterKeys: 'Master keys',
    },
    grant: {
      user: 'User',
      group: 'Department',
      bay: 'Bay',
      grantedAt: 'Granted at',
      grantedBy: 'Granted by',
      notes: 'Notes',
    },
    masterKey: {
      label: 'Label',
      targetType: 'Target type',
      target: 'Target',
      validFrom: 'Valid from',
      validTo: 'Valid until',
      revokedAt: 'Revoked at',
      assignee: 'Assigned to',
      issuedBy: 'Issued by',
      revoke: 'Revoke',
      revokeConfirmTitle: 'Revoke this master key?',
      revokeConfirmMessage: 'The key will stop unlocking immediately. This cannot be undone.',
      issued: 'Master key issued',
      passcodePromptCopy: 'Copy this passcode now — it cannot be retrieved later.',
    },
  },
  // ── Resource: Activity Log ─────────────────────────────────────
  activityLog: {
    title: 'Activity Log',
    subtitle: 'Audit feed across lockers, MQTT events, and admin actions.',
    fields: {
      occurredAt: 'When',
      severity: 'Severity',
      source: 'Source',
      kiosk: 'Locker',
      user: 'User',
      logType: 'Type',
      notes: 'Detail',
    },
    severity: {
      info: 'Info',
      warn: 'Warning',
      error: 'Error',
    },
    source: {
      web: 'Web',
      mqtt: 'MQTT',
      system: 'System',
      kiosk: 'Locker',
    },
    filterAll: 'All',
  },
} as const
